import React, { memo, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import DefinitionRow from './definition-row';
import { KeyValueDefinition } from './hooks/use-key-value-definition-table-state';

import './key-value-table.scss';

interface Props {
	state: {
		enumDefinitionError: string;
		orderedDefinitionList: KeyValueDefinition[];
		handleDefinitionChange: (index: number, newDefinition: KeyValueDefinition) => void;
		handleDelete: (deleteIndex: number) => void;
		addNewValue: () => void;
		handleMoveItem: (dragIndex: number, hoverIndex: number) => void;
		handleDroppedItem: () => void;
	};
	headers: {
		keyLabel: string;
		valueLabel: string;
		extraLabel?: string;
	};
	renderRowExtra?: (definition: KeyValueDefinition, index: number) => React.ReactNode;
}

function KeyValueTable({ state, headers, renderRowExtra }: Props) {
	const { t } = useTranslation();
	const shouldRenderExtraColumn = Boolean(headers.extraLabel && renderRowExtra);

	const {
		enumDefinitionError,
		handleDefinitionChange,
		handleDelete,
		addNewValue,
		orderedDefinitionList,
		handleMoveItem,
		handleDroppedItem
	} = state;

	const containsEmpty = useMemo(() => {
		return Boolean(orderedDefinitionList.find(({ key }) => !key.length));
	}, [orderedDefinitionList]);

	return (
		<div className="enum-definition">
			<div className="enum-definition-header">
				<div className="enum-definition-header-item value-col">{headers.keyLabel}</div>
				<div className="enum-definition-header-item name-col">{headers.valueLabel}</div>
				{shouldRenderExtraColumn && (
					<div className="enum-definition-header-item name-col">
						{headers.extraLabel ? headers.extraLabel : ''}
					</div>
				)}
			</div>
			<div className="enum-definition-list">
				<DndProvider backend={HTML5Backend}>
					<div className="reorder-columns-container" role="list">
						{orderedDefinitionList.map((item, index) => (
							<DefinitionRow
								key={item.id}
								definition={item}
								index={index}
								moveItem={handleMoveItem}
								onDropItem={handleDroppedItem}
								onDefinitionChange={handleDefinitionChange}
								onDelete={handleDelete}
								renderRowExtra={renderRowExtra}
							/>
						))}
					</div>
				</DndProvider>
			</div>
			<div className="enum-definition-add-new">
				<Button type="link" onClick={addNewValue} disabled={containsEmpty} tabIndex={0}>
					<BringgIcon iconName={BringgFontIcons.Plus} />
					{t('CUSTOM_ATTRIBUTES.ENUM_DEFINITION.ADD_NEW')}
				</Button>
			</div>
			<div className="enum-definition-errors">{enumDefinitionError}</div>
		</div>
	);
}

export default memo(KeyValueTable);
